<template>
  <div id="app" class="min-h-screen bg-gray-50 flex flex-col">
    <nav class="bg-brand-dark">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <router-link to="/" class="text-2xl font-display text-white tracking-wide">
              BOENKOP<span class="text-brand-yellow">!</span>
            </router-link>
          </div>

          <!-- Mobile menu button -->
          <div class="flex items-center md:hidden">
            <button
              @click="mobileMenuOpen = !mobileMenuOpen"
              class="inline-flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-white hover:bg-brand-dark-light focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-brand-yellow"
            >
              <span class="sr-only">Open menu</span>
              <svg v-if="!mobileMenuOpen" class="block h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg v-else class="block h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Desktop menu -->
          <div class="hidden md:flex md:items-center md:space-x-1">
            <router-link
              to="/"
              class="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium uppercase tracking-wide"
            >
              Reserveren
            </router-link>
            <router-link
              to="/mijn-reserveringen"
              class="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium uppercase tracking-wide"
            >
              Mijn Reserveringen
            </router-link>
            <router-link
              to="/evenementen"
              class="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium uppercase tracking-wide"
            >
              Evenementen
            </router-link>
            <router-link
              to="/profielen"
              class="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium uppercase tracking-wide"
            >
              Profielen
            </router-link>
            <router-link
              to="/cadeaubon"
              class="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium uppercase tracking-wide"
            >
              Cadeaubon
            </router-link>

            <!-- Admin Menu -->
            <div v-if="isAdmin" class="relative">
              <button
                @click="showAdminMenu = !showAdminMenu"
                class="text-brand-yellow hover:text-brand-yellow-hover px-3 py-2 rounded-md flex items-center text-sm font-medium uppercase tracking-wide"
              >
                Admin
                <svg class="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <div
                v-if="showAdminMenu"
                class="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-brand-dark-light ring-1 ring-black ring-opacity-5 z-50"
              >
                <div class="py-1">
                  <router-link
                    to="/admin"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Dashboard
                  </router-link>
                  <router-link
                    to="/admin/bookings"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Reserveringen
                  </router-link>
                  <router-link
                    to="/admin/customers"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Klanten
                  </router-link>
                  <router-link
                    to="/admin/settings"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Instellingen
                  </router-link>
                  <router-link
                    to="/admin/blocked-slots"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Geblokkeerde Slots
                  </router-link>
                  <router-link
                    to="/admin/pricing-rates"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Prijzen
                  </router-link>
                  <router-link
                    to="/admin/discount-codes"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Kortingscodes
                  </router-link>
                  <router-link
                    to="/admin/cadeaubonnen"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Cadeaubonnen
                  </router-link>
                  <router-link
                    to="/admin/leads"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Leads
                  </router-link>
                  <router-link
                    to="/admin/contactberichten"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Contactberichten
                  </router-link>
                  <router-link
                    to="/admin/krediet-aankopen"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Krediet-aankopen
                  </router-link>
                  <router-link
                    to="/admin/evenementen"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Evenementen
                  </router-link>
                  <router-link
                    to="/admin/profielen"
                    @click="showAdminMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Profielen
                  </router-link>
                </div>
              </div>
            </div>

            <!-- User Menu -->
            <div v-if="isAuthenticated" class="relative ml-4 border-l border-gray-600 pl-4">
              <button
                @click="showUserMenu = !showUserMenu"
                class="flex items-center space-x-2 text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm"
              >
                <span>{{ user?.name }}</span>
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <div
                v-if="showUserMenu"
                class="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-brand-dark-light ring-1 ring-black ring-opacity-5 z-50"
              >
                <div class="py-1">
                  <router-link
                    to="/profiel"
                    @click="showUserMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Mijn Profiel
                  </router-link>
                  <router-link
                    to="/mijn-evenementen"
                    @click="showUserMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Mijn Evenementen
                  </router-link>
                  <router-link
                    to="/mijn-berichten"
                    @click="showUserMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Mijn Berichten
                  </router-link>
                  <router-link
                    to="/cadeaubon-inwisselen"
                    @click="showUserMenu = false"
                    class="block px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Cadeaubon inwisselen
                  </router-link>
                  <button
                    @click="logout"
                    class="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-brand-dark-card hover:text-white"
                  >
                    Uitloggen
                  </button>
                </div>
              </div>
            </div>

            <!-- Login/Register if not authenticated -->
            <div v-if="!isAuthenticated" class="ml-4">
              <router-link
                to="/login"
                class="inline-flex items-center px-4 py-2 border border-brand-yellow text-sm font-medium rounded-md text-brand-yellow hover:bg-brand-yellow hover:text-brand-dark transition-colors uppercase tracking-wide"
              >
                Inloggen
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile menu -->
      <div v-if="mobileMenuOpen" class="md:hidden">
        <div class="px-2 pt-2 pb-3 space-y-1 bg-brand-dark-light">
          <router-link
            to="/"
            @click="mobileMenuOpen = false"
            class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
          >
            Reserveren
          </router-link>
          <router-link
            to="/mijn-reserveringen"
            @click="mobileMenuOpen = false"
            class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
          >
            Mijn Reserveringen
          </router-link>
          <router-link
            to="/evenementen"
            @click="mobileMenuOpen = false"
            class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
          >
            Evenementen
          </router-link>
          <router-link
            to="/profielen"
            @click="mobileMenuOpen = false"
            class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
          >
            Profielen
          </router-link>
          <router-link
            to="/cadeaubon"
            @click="mobileMenuOpen = false"
            class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
          >
            Cadeaubon
          </router-link>

          <!-- Mobile Admin Menu -->
          <div v-if="isAdmin" class="border-t border-gray-700 pt-2">
            <div class="px-3 py-2 text-sm font-medium text-brand-yellow uppercase tracking-wide">Admin</div>
            <router-link
              to="/admin"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Dashboard
            </router-link>
            <router-link
              to="/admin/bookings"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Reserveringen
            </router-link>
            <router-link
              to="/admin/customers"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Klanten
            </router-link>
            <router-link
              to="/admin/settings"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Instellingen
            </router-link>
            <router-link
              to="/admin/blocked-slots"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Geblokkeerde Slots
            </router-link>
            <router-link
              to="/admin/pricing-rates"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Prijzen
            </router-link>
            <router-link
              to="/admin/discount-codes"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Kortingscodes
            </router-link>
            <router-link
              to="/admin/cadeaubonnen"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Cadeaubonnen
            </router-link>
            <router-link
              to="/admin/leads"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Leads
            </router-link>
            <router-link
              to="/admin/contactberichten"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Contactberichten
            </router-link>
            <router-link
              to="/admin/krediet-aankopen"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Krediet-aankopen
            </router-link>
            <router-link
              to="/admin/evenementen"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Evenementen
            </router-link>
            <router-link
              to="/admin/profielen"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Profielen
            </router-link>
          </div>

          <!-- Mobile User Menu -->
          <div v-if="isAuthenticated" class="border-t border-gray-700 pt-2">
            <div class="px-3 py-2 text-sm font-medium text-white">{{ user?.name }}</div>
            <router-link
              to="/profiel"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Mijn Profiel
            </router-link>
            <router-link
              to="/mijn-evenementen"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Mijn Evenementen
            </router-link>
            <router-link
              to="/cadeaubon-inwisselen"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Cadeaubon inwisselen
            </router-link>
            <button
              @click="logout"
              class="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-brand-dark-card"
            >
              Uitloggen
            </button>
          </div>

          <!-- Mobile Login -->
          <div v-if="!isAuthenticated" class="border-t border-gray-700 pt-2">
            <router-link
              to="/login"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 rounded-md text-base font-medium text-brand-yellow hover:text-white"
            >
              Inloggen
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <main class="flex-1">
      <router-view />
    </main>

    <!-- Footer -->
    <footer class="bg-brand-dark mt-auto">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <!-- Company Info -->
          <div>
            <h3 class="text-2xl font-display text-white tracking-wide mb-4">BOENKOP<span class="text-brand-yellow">!</span></h3>
            <p class="text-gray-400 text-sm">
              Professionele fotostudio en videostudio te huur in Aalst. Volledig uitgerust, flexibel te boeken.
            </p>
          </div>

          <!-- Quick Links -->
          <div>
            <h3 class="text-sm font-bold text-brand-yellow uppercase tracking-wider mb-4">Studio</h3>
            <ul class="space-y-2 text-sm">
              <li>
                <router-link to="/" class="text-gray-400 hover:text-white transition-colors">
                  Reserveren
                </router-link>
              </li>
              <li>
                <router-link to="/mijn-reserveringen" class="text-gray-400 hover:text-white transition-colors">
                  Mijn Reserveringen
                </router-link>
              </li>
              <li>
                <a href="https://www.boenkop.be" target="_blank" class="text-gray-400 hover:text-white transition-colors">
                  Website
                </a>
              </li>
            </ul>
          </div>

          <!-- Contact -->
          <div>
            <h3 class="text-sm font-bold text-brand-yellow uppercase tracking-wider mb-4">Contact</h3>
            <ul class="space-y-2 text-sm text-gray-400">
              <li>Geraardsbergsestraat 31, 9300 Aalst</li>
              <li><a href="mailto:administratie@boenkop.be" class="hover:text-white transition-colors">administratie@boenkop.be</a></li>
              <li>Ma-Zo: 07:00 - 22:00</li>
            </ul>
          </div>

          <!-- Legal Links -->
          <div>
            <h3 class="text-sm font-bold text-brand-yellow uppercase tracking-wider mb-4">Juridisch</h3>
            <ul class="space-y-2 text-sm">
              <li>
                <router-link to="/algemene-voorwaarden" class="text-gray-400 hover:text-white transition-colors">
                  Algemene Voorwaarden
                </router-link>
              </li>
              <li>
                <router-link to="/privacybeleid" class="text-gray-400 hover:text-white transition-colors">
                  Privacybeleid
                </router-link>
              </li>
            </ul>
          </div>
        </div>

        <div class="border-t border-gray-700 mt-10 pt-8">
          <p class="text-center text-gray-500 text-sm">
            &copy; {{ new Date().getFullYear() }} Studio BoenkOp &mdash; Veldeman Consulting BV
          </p>
        </div>
      </div>
    </footer>

    <ToastHost />
    <ConfirmDialogHost />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import ToastHost from '@/components/ToastHost.vue'
import ConfirmDialogHost from '@/components/ConfirmDialogHost.vue'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

const authStore = useAuthStore()
const { isAuthenticated, isAdmin, user } = storeToRefs(authStore)
const router = useRouter()

const showAdminMenu = ref(false)
const showUserMenu = ref(false)
const mobileMenuOpen = ref(false)

const logout = async () => {
  showUserMenu.value = false
  mobileMenuOpen.value = false
  await authStore.logout()
  router.push('/')
}

// Close menus when clicking outside
document.addEventListener('click', (e) => {
  const target = e.target as HTMLElement
  if (!target.closest('.relative')) {
    showAdminMenu.value = false
    showUserMenu.value = false
  }
})
</script>